import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BossBadge, DifficultyBadge, TypeBadge } from "./Badges";
import { ExternalIcon, LockIcon } from "./Icons";
import { CodeBlock, InlineMarkdown, Markdown } from "./Markdown";

function Hints({ hints, onShow }) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(0);
  if (!hints || hints.length === 0) return null;

  return (
    <section className="hints">
      <h2 className="section-title">{t("level.hints")}</h2>
      <ol>
        {hints.slice(0, shown).map((hint, index) => (
          <li key={index}>
            <InlineMarkdown source={hint} />
          </li>
        ))}
      </ol>
      {shown < hints.length ? (
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => {
            setShown(shown + 1);
            onShow?.();
          }}
        >
          {t("level.showHint", { index: shown + 1, total: hints.length })}
        </button>
      ) : (
        <p className="muted small">{t("level.noMoreHints")}</p>
      )}
    </section>
  );
}

export function References({ references }) {
  const { t } = useTranslation();
  if (!references || references.length === 0) return null;
  return (
    <section>
      <h2 className="section-title">{t("level.references")}</h2>
      <ul className="references">
        {references.map((ref) => (
          <li key={ref.url}>
            <a href={ref.url} target="_blank" rel="noopener noreferrer">
              {ref.title}
              <ExternalIcon size={12} />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Review({ level, unlocked, onReveal, onLoadSolution }) {
  const { t } = useTranslation();

  if (!unlocked) {
    return (
      <div className="locked-panel">
        <LockIcon size={20} />
        <p>{t("level.reviewLocked")}</p>
        <p className="muted small">{t("level.revealWarning")}</p>
        <button type="button" className="button button--ghost" onClick={onReveal}>
          {t("level.revealSolution")}
        </button>
      </div>
    );
  }

  return (
    <>
      <section>
        <h2 className="section-title">{t("level.explanation")}</h2>
        <Markdown source={level.explanation} />
      </section>
      {level.pitfalls?.length > 0 && (
        <section>
          <h2 className="section-title">{t("level.pitfalls")}</h2>
          <ul className="pitfalls">
            {level.pitfalls.map((pitfall) => (
              <li key={pitfall}>
                <InlineMarkdown source={pitfall} />
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <div className="section-title section-title--with-action">
          <h2>{t("level.solution")}</h2>
          <button type="button" className="button button--ghost button--small" onClick={onLoadSolution}>
            {t("level.loadSolution")}
          </button>
        </div>
        <CodeBlock code={level.solution} />
      </section>
      <References references={level.references} />
    </>
  );
}

function Instructions({ level, chapter, position, isSolved, tab, onTabChange, onLoadSolution, onHint, onReveal, footer }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const tabs = level.type === "code" ? ["lesson", "task", "review"] : [];

  return (
    <section className="instructions" aria-labelledby="level-title">
      <header className="instructions__header">
        <p className="eyebrow">
          {chapter.title} · {t("level.position", position)}
        </p>
        <h1 id="level-title">{level.title}</h1>
        <div className="instructions__meta">
          {level.boss && <BossBadge />}
          <TypeBadge type={level.type} />
          <DifficultyBadge difficulty={level.difficulty} />
          {isSolved && <span className="solved-badge">{t("level.solved")}</span>}
        </div>
        {tabs.length > 0 && (
          <div className="tabs" role="tablist" aria-label={level.title}>
            {tabs.map((name) => (
              <button
                key={name}
                id={`tab-${name}`}
                type="button"
                role="tab"
                aria-selected={tab === name}
                aria-controls="instructions-panel"
                className={tab === name ? "is-active" : ""}
                onClick={() => onTabChange(name)}
              >
                {t(`level.tabs.${name}`)}
              </button>
            ))}
          </div>
        )}
      </header>

      <div
        id="instructions-panel"
        className="instructions__body"
        role={tabs.length ? "tabpanel" : undefined}
        aria-labelledby={tabs.length ? `tab-${tab}` : undefined}
      >
        {(tab === "lesson" || level.type === "quiz") && (
          <>
            {level.boss && <p className="boss-intro">{t("level.bossIntro")}</p>}
            <Markdown source={level.lesson} />
            {level.type === "quiz" && <References references={level.references} />}
            {level.type === "code" && (
              <button type="button" className="button button--primary" onClick={() => onTabChange("task")}>
                {t("level.tabs.task")} →
              </button>
            )}
          </>
        )}
        {tab === "task" && level.type === "code" && (
          <>
            <Markdown source={level.task} />
            <Hints key={level.id} hints={level.hints} onShow={onHint} />
          </>
        )}
        {tab === "review" && level.type === "code" && (
          <Review
            level={level}
            unlocked={isSolved || revealed}
            onReveal={() => {
              setRevealed(true);
              onReveal?.();
            }}
            onLoadSolution={onLoadSolution}
          />
        )}
      </div>
      {footer}
    </section>
  );
}

export default Instructions;
